import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Activity, Clock, CheckCircle, XCircle, AlertTriangle, RefreshCw, Play, Pause, Eye, Zap, Settings, History, Database, Bell, ChevronDown, Filter } from 'lucide-react';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { supabase } from '../../lib/supabase';

interface CronjobSetting {
  id: string;
  job_type: string;
  job_name: string;
  enabled: boolean;
  schedule_interval: string;
  pg_cron_job_id: number | null;
  pg_cron_job_name: string | null;
  next_execution_at: string | null;
  last_execution_at: string | null;
  last_execution_status: string | null;
  consecutive_failures: number;
  health_score: number;
  pg_cron_active: boolean | null;
  pg_cron_schedule: string | null;
  is_scheduled: boolean;
  unresolved_alert_count: number;
  executions_24h: number;
  successful_executions_24h: number;
}

interface ExecutionLog {
  id: string;
  job_type: string;
  execution_id: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  items_processed: number;
  items_succeeded: number;
  items_failed: number;
  error_message: string | null;
  triggered_by: string;
  metadata: any;
}

interface Alert {
  id: string;
  job_type: string;
  alert_type: string;
  severity: string;
  title: string;
  message: string;
  acknowledged: boolean;
  resolved: boolean;
  created_at: string;
}

interface CampaignStep {
  id: string;
  execution_log_id: string;
  campaign_id: string;
  step_number: number;
  step_name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  error_message: string | null;
  technical_feedback: any;
}

type TabType = 'uitvoeringen' | 'instellingen' | 'alerts' | 'beheer';

export function AdminCronjobMonitor() {
  const { authUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('uitvoeringen');
  const [cronjobSettings, setCronjobSettings] = useState<CronjobSetting[]>([]);
  const [recentExecutions, setRecentExecutions] = useState<ExecutionLog[]>([]);
  const [unresolvedAlerts, setUnresolvedAlerts] = useState<Alert[]>([]);
  const [selectedExecution, setSelectedExecution] = useState<ExecutionLog | null>(null);
  const [campaignSteps, setCampaignSteps] = useState<CampaignStep[]>([]);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [showExecutionDetails, setShowExecutionDetails] = useState(false);
  const [triggeringJob, setTriggeringJob] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [jobTypeFilter, setJobTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadData();
    // Load saved filters from localStorage
    const savedFilters = localStorage.getItem('cronjob-filters');
    if (savedFilters) {
      try {
        const { jobType, status } = JSON.parse(savedFilters);
        if (jobType) setJobTypeFilter(jobType);
        if (status) setStatusFilter(status);
      } catch (e) {
        console.error('Failed to load saved filters:', e);
      }
    }
  }, []);

  // Save filters to localStorage
  useEffect(() => {
    localStorage.setItem('cronjob-filters', JSON.stringify({
      jobType: jobTypeFilter,
      status: statusFilter
    }));
  }, [jobTypeFilter, statusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCronjobSettings(),
        loadRecentExecutions(),
        loadUnresolvedAlerts(),
      ]);
    } catch (error) {
      console.error('Error loading cronjob data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCronjobSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('cronjob_status_overview')
        .select('*')
        .order('job_name');

      if (error) throw error;
      setCronjobSettings(data || []);
    } catch (error) {
      console.error('Failed to load cronjob settings:', error);
      setCronjobSettings([]);
    }
  };

  const loadRecentExecutions = async () => {
    try {
      let { data, error } = await supabase
        .from('cronjob_execution_details')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(50);

      if (error && error.message?.includes('does not exist')) {
        const result = await supabase
          .from('cronjob_execution_logs')
          .select('*')
          .order('started_at', { ascending: false })
          .limit(50);

        data = result.data;
        error = result.error;
      }

      if (error) throw error;
      setRecentExecutions(data || []);
    } catch (error) {
      console.error('Failed to load recent executions:', error);
      setRecentExecutions([]);
    }
  };

  const loadUnresolvedAlerts = async () => {
    try {
      const { data, error } = await supabase
        .from('cronjob_alerts')
        .select('*')
        .eq('resolved', false)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setUnresolvedAlerts(data || []);
    } catch (error) {
      console.error('Failed to load unresolved alerts:', error);
      setUnresolvedAlerts([]);
    }
  };

  const loadDiagnostics = async () => {
    try {
      const { data, error } = await supabase.rpc('get_cronjob_diagnostics');
      if (error) throw error;
      setDiagnostics(data);
    } catch (error: any) {
      console.error('Failed to load diagnostics:', error);
      alert(`Fout bij laden diagnostics: ${error.message || 'Unknown error'}`);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleToggleJob = async (jobType: string, currentEnabled: boolean) => {
    const action = currentEnabled ? 'uitschakelen' : 'inschakelen';
    if (!confirm(`Weet je zeker dat je deze cronjob wilt ${action}?`)) {
      return;
    }

    setActionLoading(true);
    try {
      if (currentEnabled) {
        const { data, error } = await supabase.rpc('disable_cronjob', {
          p_job_type: jobType,
          p_reason: 'Handmatig uitgeschakeld door admin'
        });

        if (error) throw error;
        if (data && !data.success) {
          throw new Error(data.error || 'Failed to disable cronjob');
        }

        if (data && data.warning) {
          alert(`Cronjob uitgeschakeld, maar waarschuwing:\n${data.warning}`);
        } else {
          alert('Cronjob succesvol uitgeschakeld!');
        }
      } else {
        const { data, error } = await supabase.rpc('enable_cronjob', {
          p_job_type: jobType
        });

        if (error) throw error;
        if (data && !data.success) {
          if (data.action_needed === 'sync_required') {
            alert('Er is geen pg_cron job gevonden. Neem contact op met een beheerder.');
            return;
          }
          throw new Error(data.error || 'Failed to enable cronjob');
        }

        if (data && data.warning) {
          alert(`Cronjob ingeschakeld, maar waarschuwing:\n${data.warning}`);
        } else {
          alert('Cronjob succesvol ingeschakeld!');
        }
      }

      await loadCronjobSettings();
    } catch (error: any) {
      alert(`Fout bij het ${action}: ${error.message}`);
    }
    setActionLoading(false);
  };

  // Bepaal of een cronjob handmatig getriggerd kan worden
  const canManualTrigger = (jobType: string): boolean => {
    return ['scheduled_campaigns', 'scheduled_blogs'].includes(jobType);
  };

  const handleManualTrigger = async (jobType: string) => {
    if (!canManualTrigger(jobType)) {
      alert('Deze cronjob wordt automatisch uitgevoerd en kan niet handmatig getriggerd worden.');
      return;
    }

    if (!authUser?.id) {
      alert('Je moet ingelogd zijn als admin om een cronjob te triggeren.');
      return;
    }

    if (!confirm(`Weet je zeker dat je de cronjob "${jobType}" handmatig wilt triggeren?\n\nDit zal de cronjob onmiddellijk uitvoeren voor test/debug doeleinden.`)) {
      return;
    }

    setTriggeringJob(jobType);
    try {
      const { data, error } = await supabase.rpc('rpc_manual_trigger_cronjob', {
        p_job_type: jobType,
        p_limit: null,
        p_test_mode: false,
        p_admin_user_id: authUser.id
      });

      if (error) throw error;
      if (data && !data.success) {
        throw new Error(data.error || 'Failed to trigger cronjob');
      }

      const response = data?.response || {};
      const triggered = response.triggered || response.processed || 0;
      const failed = response.failed || 0;

      alert(`Cronjob succesvol getriggerd!\n\nResultaten:\n- Items verwerkt: ${triggered}\n- Items gefaald: ${failed}`);

      await loadData();
    } catch (error: any) {
      console.error('Failed to trigger cronjob:', error);
      alert(`Fout bij triggeren: ${error.message}`);
    }
    setTriggeringJob(null);
  };


  const handleAcknowledgeAlert = async (alertId: string) => {
    try {
      const { error } = await supabase
        .from('cronjob_alerts')
        .update({
          acknowledged: true,
          acknowledged_at: new Date().toISOString(),
          acknowledged_by: authUser?.id || null,
        })
        .eq('id', alertId);

      if (error) throw error;
      await loadUnresolvedAlerts();
    } catch (error: any) {
      console.error('Failed to acknowledge alert:', error);
      alert(`Fout bij het bevestigen: ${error.message}`);
    }
  };

  const handleResolveAlert = async (alertId: string) => {
    try {
      const { error } = await supabase
        .from('cronjob_alerts')
        .update({
          resolved: true,
          resolved_at: new Date().toISOString(),
        })
        .eq('id', alertId);

      if (error) throw error;
      await loadUnresolvedAlerts();
    } catch (error: any) {
      console.error('Failed to resolve alert:', error);
      alert(`Fout bij het oplossen: ${error.message}`);
    }
  };

  const viewExecutionDetails = async (execution: ExecutionLog) => {
    setSelectedExecution(execution);
    setShowExecutionDetails(true);

    // Load campaign steps if this is a campaign_send job
    if (execution.job_type === 'campaign_send') {
      setLoadingSteps(true);
      try {
        const { data, error } = await supabase
          .from('campaign_send_steps')
          .select('*')
          .eq('execution_log_id', execution.id)
          .order('step_number');

        if (error) throw error;
        setCampaignSteps(data || []);
      } catch (error) {
        console.error('Failed to load campaign steps:', error);
        setCampaignSteps([]);
      } finally {
        setLoadingSteps(false);
      }
    } else {
      setCampaignSteps([]);
    }
  };

  const viewExecutionsForJob = (jobType: string) => {
    setJobTypeFilter(jobType);
    setStatusFilter('all');
    setActiveTab('uitvoeringen');
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDuration = (ms: number | null) => {
    if (!ms && ms !== 0) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      'completed': 'bg-green-100 text-green-700',
      'running': 'bg-blue-100 text-blue-700',
      'failed': 'bg-red-100 text-red-700',
      'timeout': 'bg-orange-100 text-orange-700',
    };

    const icons: Record<string, React.ReactNode> = {
      'completed': <CheckCircle className="w-3 h-3" />,
      'running': <Activity className="w-3 h-3 animate-spin" />,
      'failed': <XCircle className="w-3 h-3" />,
      'timeout': <Clock className="w-3 h-3" />,
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {icons[status]}
        {status}
      </span>
    );
  };

  const getStatusColor = (setting: CronjobSetting) => {
    if (!setting.enabled) return 'bg-gray-400';
    if (!setting.is_scheduled || setting.pg_cron_active === false) return 'bg-red-500';
    if (setting.last_execution_status === 'failed') return 'bg-amber-500';
    return 'bg-green-500';
  };

  const getSeverityBadge = (severity: string) => {
    const styles: Record<string, string> = {
      critical: 'bg-red-100 text-red-700 border-red-300',
      error: 'bg-orange-100 text-orange-700 border-orange-300',
      warning: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      info: 'bg-blue-100 text-blue-700 border-blue-300',
    };
    return `inline-block px-2 py-1 rounded border text-xs font-semibold uppercase ${styles[severity] || 'bg-gray-100 text-gray-700'}`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner text="Cronjob monitoring laden..." />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-4xl font-bold text-[#231F20] mb-2">Cronjob Monitor</h1>
            <p className="text-gray-600">Monitoring en controle van alle geautomatiseerde taken</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Vernieuwen
          </button>
        </div>

        <div className="border-b border-gray-200 mb-6">
          <div className="flex gap-4">
            {[
              { id: 'uitvoeringen', label: 'Uitvoeringen', icon: Activity },
              { id: 'instellingen', label: 'Instellingen', icon: Settings },
              { id: 'alerts', label: 'Alerts', icon: Bell, badge: unresolvedAlerts.length },
              { id: 'beheer', label: 'Diagnostics', icon: Database },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium transition-colors relative ${
                    activeTab === tab.id
                      ? 'border-[#FF6B35] text-[#FF6B35]'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {tab.badge && tab.badge > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {activeTab === 'uitvoeringen' && (
          <div className="card">
            <h2 className="text-2xl font-bold text-[#231F20] mb-4">Recente Uitvoeringen</h2>

            {recentExecutions.length === 0 ? (
              <div className="text-center py-12">
                <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">Geen executions gevonden</p>
              </div>
            ) : (
              <>
                {/* Filters */}
                <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Job Type Filter */}
                    <div className="relative">
                      <select
                        value={jobTypeFilter}
                        onChange={(e) => setJobTypeFilter(e.target.value)}
                        className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">Alle Cronjobs</option>
                        {cronjobSettings.map(setting => (
                          <option key={setting.job_type} value={setting.job_type}>
                            {setting.job_name}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                    </div>

                    {/* Status Filter */}
                    <div className="relative">
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">Alle Statussen</option>
                        <option value="completed">✓ Geslaagd</option>
                        <option value="failed">✗ Mislukt</option>
                        <option value="running">⟳ Actief</option>
                        <option value="timeout">⏱ Timeout</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                    </div>

                    {/* Reset Filters Button */}
                    {(jobTypeFilter !== 'all' || statusFilter !== 'all') && (
                      <button
                        onClick={() => {
                          setJobTypeFilter('all');
                          setStatusFilter('all');
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 font-medium"
                      >
                        <XCircle className="w-4 h-4" />
                        Reset Filters
                      </button>
                    )}
                  </div>

                  {/* Results Count */}
                  <div className="text-sm text-gray-600 flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    <span>
                      {(() => {
                        const filtered = recentExecutions.filter(execution => {
                          if (jobTypeFilter !== 'all' && execution.job_type !== jobTypeFilter) return false;
                          if (statusFilter !== 'all' && execution.status !== statusFilter) return false;
                          return true;
                        });
                        return `${filtered.length} van ${recentExecutions.length} executions`;
                      })()}
                    </span>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Job Type</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Start</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Eind</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Duur</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Items</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Acties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentExecutions
                        .filter(execution => {
                          if (jobTypeFilter !== 'all' && execution.job_type !== jobTypeFilter) return false;
                          if (statusFilter !== 'all' && execution.status !== statusFilter) return false;
                          return true;
                        })
                        .map((execution) => (
                      <tr key={execution.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm font-mono text-gray-700">
                          {execution.job_type}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">
                          {formatTime(execution.started_at)}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">
                          {formatTime(execution.completed_at)}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-mono text-gray-700">
                          {formatDuration(execution.duration_ms)}
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(execution.status)}
                        </td>
                        <td className="py-3 px-4 text-right text-sm">
                          <span className="text-green-600 font-medium">{execution.items_succeeded}</span>
                          {execution.items_failed > 0 && (
                            <span className="text-red-600 font-medium"> / {execution.items_failed}</span>
                          )}
                          <span className="text-gray-500"> / {execution.items_processed}</span>
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => viewExecutionDetails(execution)}
                            className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'instellingen' && (
          <div className="card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Cronjob</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Schedule</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Laatste Run</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {cronjobSettings.map((setting) => {
                    const statusColor = getStatusColor(setting);

                    return (
                      <tr key={setting.id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${statusColor}`} />
                            <span className="text-sm font-medium text-gray-700">
                              {setting.enabled ? 'Actief' : 'Uit'}
                            </span>
                            {setting.unresolved_alert_count > 0 && (
                              <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                                {setting.unresolved_alert_count}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-900 text-sm">{setting.job_name}</span>
                            <span className="text-xs text-gray-500 font-mono">{setting.job_type}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm font-mono text-gray-700">{setting.schedule_interval}</span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col text-sm">
                            {!setting.is_scheduled ? (
                              <span className="text-red-500">Niet ingepland in pg_cron</span>
                            ) : setting.last_execution_at ? (
                              <>
                                <span className="text-gray-700">{formatTime(setting.last_execution_at)}</span>
                                <span className="text-xs text-gray-500">{formatDate(setting.last_execution_at)}</span>
                              </>
                            ) : (
                              <span className="text-gray-400">Nog niet uitgevoerd</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => viewExecutionsForJob(setting.job_type)}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="Bekijk uitvoeringsgeschiedenis"
                            >
                              <History className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleManualTrigger(setting.job_type)}
                              disabled={!canManualTrigger(setting.job_type) || triggeringJob === setting.job_type}
                              className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                                canManualTrigger(setting.job_type)
                                  ? 'text-blue-600 hover:bg-blue-50'
                                  : 'text-gray-400 cursor-not-allowed'
                              }`}
                              title={
                                canManualTrigger(setting.job_type)
                                  ? 'Handmatig triggeren'
                                  : 'Deze cronjob wordt automatisch uitgevoerd en kan niet handmatig getriggerd worden'
                              }
                            >
                              {triggeringJob === setting.job_type ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Play className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleToggleJob(setting.job_type, setting.enabled)}
                              disabled={actionLoading}
                              className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                                setting.enabled
                                  ? 'text-gray-600 hover:bg-gray-100'
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                              title={setting.enabled ? 'Uitschakelen' : 'Inschakelen'}
                            >
                              {setting.enabled ? (
                                <Pause className="w-4 h-4" />
                              ) : (
                                <Play className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'alerts' && (
          <div className="card">
            <h2 className="text-2xl font-bold text-[#231F20] mb-4">Onopgeloste Alerts</h2>

            {unresolvedAlerts.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-600">Geen onopgeloste alerts</p>
              </div>
            ) : (
              <div className="space-y-3">
                {unresolvedAlerts.map((alert) => (
                  <div key={alert.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={getSeverityBadge(alert.severity)}>{alert.severity}</span>
                          <span className="text-xs text-gray-500 font-mono">{alert.job_type}</span>
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-1">{alert.title}</h4>
                        <p className="text-sm text-gray-700">{alert.message}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {formatDateTime(alert.created_at)}
                        </p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        {!alert.acknowledged && (
                          <button
                            onClick={() => handleAcknowledgeAlert(alert.id)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Bevestig"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleResolveAlert(alert.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded"
                          title="Oplossen"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'beheer' && (
          <div className="space-y-6">
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-[#231F20]">System Diagnostics</h2>
                <button
                  onClick={loadDiagnostics}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  {diagnostics ? 'Vernieuwen' : 'Laden'}
                </button>
              </div>

              {diagnostics ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-900 mb-2">Extensions</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">pg_cron:</span>
                          <span className={`font-medium ${diagnostics.pg_cron_enabled ? 'text-green-600' : 'text-red-600'}`}>
                            {diagnostics.pg_cron_enabled ? '✓ Actief' : '✗ Niet actief'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">pg_net:</span>
                          <span className={`font-medium ${diagnostics.pg_net_enabled ? 'text-green-600' : 'text-red-600'}`}>
                            {diagnostics.pg_net_enabled ? '✓ Actief' : '✗ Niet actief'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-900 mb-2">Pg_cron Jobs</h3>
                      <div className="space-y-2 text-sm">
                        {diagnostics.cron_jobs && diagnostics.cron_jobs.length > 0 ? (
                          diagnostics.cron_jobs.map((job: any) => (
                            <div key={job.jobid} className="flex items-center justify-between">
                              <span className="font-mono text-xs text-gray-900">{job.jobname}</span>
                              <span className={`text-xs font-medium ${job.active ? 'text-green-600' : 'text-red-600'}`}>
                                {job.active ? 'Actief' : 'Inactief'}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="text-red-600 font-medium">⚠️ Geen pg_cron jobs gevonden!</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {(!diagnostics.pg_cron_enabled || !diagnostics.pg_net_enabled ||
                    !diagnostics.cron_jobs || diagnostics.cron_jobs.length === 0) && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-orange-900 mb-1">Configuratie Problemen</h4>
                          <p className="text-sm text-orange-800 mb-2">
                            Er zijn problemen gevonden met de cronjob configuratie. Neem contact op met een beheerder.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">Klik op "Laden" om diagnostics te bekijken</p>
              )}
            </div>
          </div>
        )}
      </div>

      {showExecutionDetails && selectedExecution && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-[#231F20] mb-2">Cronjob Uitvoering Details</h2>
                  <p className="text-sm text-gray-600 font-mono">{selectedExecution.job_type}</p>
                  <p className="text-sm text-gray-600">
                    Execution ID: <span className="font-mono text-xs">{selectedExecution.execution_id}</span>
                  </p>
                </div>
                <button
                  onClick={() => setShowExecutionDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-gray-700">
                    <Clock className="w-5 h-5 text-gray-500" />
                    <span className="font-semibold">Start:</span>
                    <span className="font-mono">{formatDateTime(selectedExecution.started_at)}</span>
                  </div>

                  <div className="border-l-4 border-gray-300 pl-6 py-4 space-y-3">
                    {selectedExecution.items_processed === 0 ? (
                      <div className="flex items-start gap-3 text-gray-600">
                        <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Geen script uitgevoerd</p>
                          <p className="text-sm">De cronjob draaide maar vond geen items om te verwerken</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3">
                          <Activity className="w-5 h-5 text-blue-500" />
                          <span className="text-gray-700">
                            {selectedExecution.items_processed} item(s) gevonden
                          </span>
                        </div>
                        {selectedExecution.items_succeeded > 0 && (
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <span className="text-gray-700">
                              {selectedExecution.items_succeeded} item(s) succesvol verwerkt
                            </span>
                          </div>
                        )}
                        {selectedExecution.items_failed > 0 && (
                          <div className="flex items-center gap-3">
                            <XCircle className="w-5 h-5 text-red-500" />
                            <span className="text-gray-700">
                              {selectedExecution.items_failed} item(s) gefaald
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-gray-700">
                    <Clock className="w-5 h-5 text-gray-500" />
                    <span className="font-semibold">Eind:</span>
                    <span className="font-mono">{formatDateTime(selectedExecution.completed_at)}</span>
                  </div>

                  <div className="pt-4 border-t border-gray-300 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getStatusBadge(selectedExecution.status)}
                      <span className="text-sm text-gray-600">
                        Duur: <span className="font-mono font-medium">{formatDuration(selectedExecution.duration_ms)}</span>
                      </span>
                    </div>
                    {selectedExecution.triggered_by === 'manual' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        <Zap className="w-3 h-3" />
                        Handmatig
                      </span>
                    )}
                  </div>
                </div>

                {selectedExecution.error_message && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-semibold text-red-900 mb-1">Error</div>
                        <div className="text-sm text-red-800">{selectedExecution.error_message}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Campaign Send Steps */}
              {selectedExecution.job_type === 'campaign_send' && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-[#231F20] mb-4">Verzend Stappen</h3>
                  {loadingSteps ? (
                    <div className="flex items-center justify-center py-8">
                      <LoadingSpinner />
                    </div>
                  ) : campaignSteps.length > 0 ? (
                    <div className="space-y-3">
                      {campaignSteps.map((step) => (
                        <div
                          key={step.id}
                          className={`border rounded-lg p-4 ${
                            step.status === 'completed'
                              ? 'bg-green-50 border-green-200'
                              : step.status === 'failed'
                              ? 'bg-red-50 border-red-200'
                              : step.status === 'in_progress'
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="mt-0.5">
                                {step.status === 'completed' && (
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                )}
                                {step.status === 'failed' && (
                                  <XCircle className="w-5 h-5 text-red-600" />
                                )}
                                {step.status === 'in_progress' && (
                                  <Activity className="w-5 h-5 text-blue-600 animate-spin" />
                                )}
                                {step.status === 'pending' && (
                                  <Clock className="w-5 h-5 text-gray-400" />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">
                                    {step.step_number}. {step.step_name}
                                  </span>
                                  {step.duration_ms !== null && (
                                    <span className="text-xs text-gray-500">
                                      ({formatDuration(step.duration_ms)})
                                    </span>
                                  )}
                                </div>
                                {step.error_message && (
                                  <div className="mt-2 text-sm text-red-700">
                                    <strong>Error:</strong> {step.error_message}
                                  </div>
                                )}
                                {step.technical_feedback && Object.keys(step.technical_feedback).length > 0 && (
                                  <div className="mt-2 text-xs text-gray-600 font-mono bg-white bg-opacity-50 p-2 rounded">
                                    {JSON.stringify(step.technical_feedback, null, 2)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 text-center py-4">
                      Geen stappen gevonden voor deze uitvoering
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => setShowExecutionDetails(false)}
                className="btn-secondary w-full"
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
